import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Block } from "./Block";
import { Obstacle } from "./Obstacle";
import { Note, ObstacleNote } from "./beatmap";
import { useGameStore } from "./useGameStore";
import { useTrack } from "../audio/useTrack";
import { preloadSlashSounds } from "../audio/useSlashSound";
import { TRAVEL_TIME, KATA_BONUS_RATIO } from "./constants";
import { CAMPAIGN_PHASES } from "./campaign";
import { CAMPAIGN_TARGET_DEFAULTS, getTargetTravelTime } from "./targetSystem";

interface ActiveNote extends Note {
  id: number;
  spawnCamY: number;
  scheduledSpawnTime: number;
}

interface ActiveObstacle extends ObstacleNote {
  id: number;
}

interface GameManagerProps {
  onWin: () => void;
}

const WIN_GRACE_SECONDS = 0.75;

// Safety net, not a tuning knob: min_gap/travel-time already keeps steady-
// state concurrency well under this (~8 at phase X's tightest min_gap of
// 0.28s), so this only ever engages on a pathological edge case (e.g. an
// obstacle landing inside a tight kata run) — never in normal authored
// content. Mirrors survival.ts's maxConcurrentNotes, which campaign lacked.
const MAX_CONCURRENT_NOTES = 10;

export function GameManager({ onWin }: GameManagerProps) {
  const phase = useGameStore((s) => s.phase);
  const selectedPhaseIndex = useGameStore((s) => s.selectedPhaseIndex);
  const [activeNotes, setActiveNotes] = useState<ActiveNote[]>([]);
  const [activeObstacles, setActiveObstacles] = useState<ActiveObstacle[]>([]);
  const cursor = useRef(0);
  const obstacleCursor = useRef(0);
  const wonRef = useRef(false);
  const seqBonusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { camera } = useThree();

  const seqHits = useRef(new Map<number, number>());
  const seqPoints = useRef(new Map<number, number>());
  const seqFailed = useRef(new Set<number>());

  const phaseConfig = CAMPAIGN_PHASES[selectedPhaseIndex] ?? CAMPAIGN_PHASES[0];
  const beatmap = phaseConfig.beatmap;
  const targetDefaults =
    CAMPAIGN_TARGET_DEFAULTS[selectedPhaseIndex] ?? CAMPAIGN_TARGET_DEFAULTS[0];
  const { play, stop, pause, resume, getCurrentTime, hasEnded } = useTrack(
    phaseConfig.track,
  );
  const {
    addScore,
    setSeqBonus,
    unlockKata,
    setRunKataTotal,
    recordKataLanded,
  } = useGameStore.getState();

  const prevPhase = useRef(phase);

  const seqTotals = useMemo(() => {
    const totals = new Map<number, number>();
    for (const note of beatmap.notes) {
      if (note.seq != null) {
        totals.set(note.seq, (totals.get(note.seq) ?? 0) + 1);
      }
    }
    return totals;
  }, [beatmap]);

  const sortedObstacles = useMemo(
    () => (beatmap.obstacles ?? []).slice().sort((a, b) => a.t - b.t),
    [beatmap],
  );

  const lastEventTime = useMemo(() => {
    const lastNote = beatmap.notes.reduce(
      (latest, note) => Math.max(latest, note.t),
      0,
    );
    const lastObstacle = sortedObstacles.reduce(
      (latest, obstacle) => Math.max(latest, obstacle.t),
      0,
    );
    return Math.max(lastNote, lastObstacle);
  }, [beatmap, sortedObstacles]);

  useEffect(() => {
    return () => {
      if (seqBonusTimeoutRef.current != null) {
        clearTimeout(seqBonusTimeoutRef.current);
        seqBonusTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const prev = prevPhase.current;
    prevPhase.current = phase;

    if (phase === "countdown" || phase === "playing") {
      preloadSlashSounds().catch(() => undefined);
    }

    if (phase === "playing" && prev !== "playing") {
      if (prev === "paused") {
        // Resume — keep cursors, notes and clock intact
        resume();
      } else {
        // Fresh start (from countdown) — the only other legal entry into
        // "playing". Gating on prev !== "playing" above stops a duplicate
        // effect run (e.g. two rapid toggle-pause presses batched into one
        // render, so this effect only sees the net "playing" -> "playing"
        // change) from being misread as a fresh start and wiping an
        // in-progress run's cursor/notes/audio position.
        cursor.current = 0;
        obstacleCursor.current = 0;
        wonRef.current = false;
        seqHits.current.clear();
        seqPoints.current.clear();
        seqFailed.current.clear();
        if (seqBonusTimeoutRef.current != null) {
          clearTimeout(seqBonusTimeoutRef.current);
          seqBonusTimeoutRef.current = null;
        }
        setSeqBonus(null);
        setRunKataTotal(seqTotals.size);
        setActiveNotes([]);
        setActiveObstacles([]);
        play();
      }
    }
    if (phase === "paused") {
      pause();
    }
    if (phase === "menu" || phase === "win" || phase === "fail") {
      stop();
      cursor.current = 0;
      obstacleCursor.current = 0;
      wonRef.current = false;
      seqHits.current.clear();
      seqPoints.current.clear();
      seqFailed.current.clear();
      if (seqBonusTimeoutRef.current != null) {
        clearTimeout(seqBonusTimeoutRef.current);
        seqBonusTimeoutRef.current = null;
      }
      setSeqBonus(null);
      setActiveNotes([]);
      setActiveObstacles([]);
    }
  }, [phase, beatmap]);

  function handleRemove(
    noteId: number,
    wasHit: boolean,
    seq?: number,
    points = 0,
  ) {
    setActiveNotes((prev) => prev.filter((n) => n.id !== noteId));

    if (seq == null) return;

    if (!wasHit) {
      seqFailed.current.add(seq);
      return;
    }

    if (seqFailed.current.has(seq)) return;

    const hits = (seqHits.current.get(seq) ?? 0) + 1;
    seqHits.current.set(seq, hits);
    const earned = (seqPoints.current.get(seq) ?? 0) + points;
    seqPoints.current.set(seq, earned);

    const total = seqTotals.get(seq) ?? 0;
    if (hits === total) {
      const bonus = Math.round(earned * KATA_BONUS_RATIO);
      addScore(bonus);
      setSeqBonus(bonus);
      unlockKata(seq);
      recordKataLanded(bonus);
      if (seqBonusTimeoutRef.current != null)
        clearTimeout(seqBonusTimeoutRef.current);
      seqBonusTimeoutRef.current = setTimeout(() => {
        setSeqBonus(null);
        seqBonusTimeoutRef.current = null;
      }, 1200);
    }
  }

  useFrame(() => {
    if (phase !== "playing") return;

    const t = getCurrentTime();
    const toSpawn: ActiveNote[] = [];
    const toSpawnObs: ActiveObstacle[] = [];
    const camY = camera.position.y;

    while (
      cursor.current < beatmap.notes.length &&
      activeNotes.length + toSpawn.length < MAX_CONCURRENT_NOTES
    ) {
      const noteId = cursor.current;
      const note = beatmap.notes[noteId];
      const travelTime = getTargetTravelTime(
        note.speed ?? targetDefaults.speed,
      );
      const scheduledSpawnTime = note.t - travelTime;
      if (scheduledSpawnTime > t) break;

      toSpawn.push({
        ...note,
        id: noteId,
        spawnCamY: camY,
        scheduledSpawnTime,
      });
      cursor.current++;
    }

    while (
      obstacleCursor.current < sortedObstacles.length &&
      sortedObstacles[obstacleCursor.current].t - TRAVEL_TIME <= t
    ) {
      const obstacleId = obstacleCursor.current;
      toSpawnObs.push({ ...sortedObstacles[obstacleId], id: obstacleId });
      obstacleCursor.current++;
    }

    if (toSpawn.length > 0) setActiveNotes((prev) => [...prev, ...toSpawn]);
    if (toSpawnObs.length > 0)
      setActiveObstacles((prev) => [...prev, ...toSpawnObs]);

    const allNotesSpawned = cursor.current >= beatmap.notes.length;
    const allObstaclesSpawned =
      obstacleCursor.current >= sortedObstacles.length;
    const noActiveNotes = activeNotes.length === 0 && toSpawn.length === 0;
    const noActiveObstacles =
      activeObstacles.length === 0 && toSpawnObs.length === 0;
    const timelineComplete = hasEnded || t >= lastEventTime + WIN_GRACE_SECONDS;

    if (
      !wonRef.current &&
      timelineComplete &&
      allNotesSpawned &&
      allObstaclesSpawned &&
      noActiveNotes &&
      noActiveObstacles
    ) {
      wonRef.current = true;
      onWin();
    }
  });

  // Keep blocks mounted while paused so they freeze in place (and resume from
  // the same spot) instead of unmounting and respawning at SPAWN_Z.
  if (phase !== "playing" && phase !== "paused") return null;

  return (
    <>
      {activeNotes.map((note) => (
        <Block
          key={`note-${note.id}`}
          note={note}
          noteId={note.id}
          spawnCamY={note.spawnCamY}
          scheduledSpawnTime={note.scheduledSpawnTime}
          targetDefaults={targetDefaults}
          getCurrentTime={getCurrentTime}
          onRemove={(wasHit, points) =>
            handleRemove(note.id, wasHit, note.seq, points)
          }
        />
      ))}
      {activeObstacles.map((obs) => (
        <Obstacle
          key={`obs-${obs.id}`}
          note={obs}
          obstacleId={obs.id}
          getCurrentTime={getCurrentTime}
          onRemove={() =>
            setActiveObstacles((prev) => prev.filter((o) => o.id !== obs.id))
          }
        />
      ))}
    </>
  );
}
